// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * D3 acceptance criterion 28 -- annotation data types.
 *
 * An annotation is a piece of visual chrome that sits *on top of* the
 * canvas but is *not* part of the underlying model data.  The schema
 * here is intentionally minimal for C28; later D3 criteria (C29+) will
 * extend the discriminated union with additional kinds (arrows,
 * highlights, freehand, etc.) and a persistence layer.
 *
 * Two design rules govern this file and prevent annotation work from
 * polluting model data:
 *
 *   1. Annotations carry their own `uid` namespace, distinct from
 *      view-element UIDs.  This keeps annotation IDs from colliding
 *      with `ViewElement.uid` in selection / dispatch code.  Callers
 *      that allocate annotation UIDs should not draw from the engine's
 *      view-element UID pool.
 *
 *   2. The exported `Annotation` type is the *only* surface the
 *      overlay layer consumes.  No engine type (Variable, View,
 *      LinkViewElement) appears in this file -- that separation is
 *      what lets D3 criterion 28 promise "without modifying the
 *      underlying model data."
 */

/**
 * A text annotation: a short label rendered at a fixed (x, y)
 * position.  Useful for callouts, captions, and explanatory notes
 * that the user wants to add to a diagram without making them part of
 * the simulation model.
 */
export interface TextAnnotation {
  readonly uid: number;
  readonly kind: 'text';
  readonly x: number;
  readonly y: number;
  readonly text: string;
}

/**
 * A box annotation: a rectangular region drawn over the diagram.
 * Useful for grouping a set of related variables visually without
 * affecting the engine's notion of structural grouping.
 */
export interface BoxAnnotation {
  readonly uid: number;
  readonly kind: 'box';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type Annotation = TextAnnotation | BoxAnnotation;

/**
 * Type-guard helpers so consumers don't have to inline the discriminant
 * check.  These keep `Annotation` extensible: adding a new kind in a
 * later criterion only requires adding a guard, not editing every
 * consumer's switch statement.
 */
export function isTextAnnotation(a: Annotation): a is TextAnnotation {
  return a.kind === 'text';
}

export function isBoxAnnotation(a: Annotation): a is BoxAnnotation {
  return a.kind === 'box';
}
